/**
 * Area-mode lifecycle.
 *
 * The polygonSelection tool is a *drawing* tool — it places a polygon and then
 * hands off to area-mode, which is the persistent UI **state** that owns:
 *   - the polygon entity in the viewer (not removed until area-mode exits)
 *   - the masts found inside the polygon (`mapToolsStore.selectedMapEntities`)
 *
 * Crucially, area-mode is **not** a tool: it doesn't lock Cesium input. Normal
 * entity selection, the Toolbar, and other map tools all stay available while
 * a polygon is drawn. The polygon simply persists until the user explicitly exits.
 *
 * Section info, load cases and the section calculation belong to path-mode —
 * see `pathMode.ts`.
 *
 * State lives in `area-mode-state.svelte.ts` as a runes-based singleton.
 * This module is the orchestration layer that ties together that state with
 * the Cesium viewer and DOM listeners.
 */

import { get } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import { isAnyToolEnabled, mapToolsStore } from '../map-tools-store';
import { toggleEscapeToolListener } from '../map-tools';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { areaModeState } from './area-mode-state.svelte';
import { deactivatePathMode } from './pathMode';
import { pathModeState } from './path-mode-state.svelte';
import type { Entity, Viewer } from 'cesium';

// ── Public API ──────────────────────────────────────────────────────────────

export type AreaModeActivationOptions = {
	polygonEntity: Entity;
	mastpoints: Entity[];
	polygonLngLat: [number, number][];
	polygon: { type: 'Polygon'; coordinates: [number, number][][] };
};

/**
 * Called by polygonSelectionTool.finalisePolygon once the polygon is closed.
 * Takes ownership of the polygon entity and flips the area-mode state on.
 */
export function activateAreaMode(opts: AreaModeActivationOptions): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	// Area-mode and path-mode share the right-hand panel slot — never both.
	if (pathModeState.active) deactivatePathMode();

	// selectedMapEntities stays on mapToolsStore — it's shared across many
	// tools/UIs and isn't owned by area-mode in particular.
	mapToolsStore.setSelectedMapEntities(opts.mastpoints);

	areaModeState.active = true;
	areaModeState.polygon = opts.polygon;
	areaModeState.polygonEntity = opts.polygonEntity;
	areaModeState.setBoundariesAndArea(opts.polygonLngLat);
	areaModeState.panelMinimized = false;

	// polygonSelection (drawing) is done — tear it down.
	mapToolsStore.update((store) => {
		store.polygonSelection.toolEnabled = false;
		store.polygonSelection.deactivateTool = () => {};
		return store;
	});

	// Esc → confirm-and-exit. Routed via a dedicated listener (not the shared
	// tool-Esc handler) because area-mode isn't a tool — the shared listener
	// would never fire for it.
	toggleEscapeToolListener({ toggleOn: false });
	document.addEventListener('keydown', handleAreaModeEsc, false);
}

/** Confirm + deactivate. Wired to Esc and the "Lukk" button. */
export function requestExitAreaMode(): void {
	confirmModal(
		'Er du sikker på at du vil gå ut av områdemodus. Områdemarkeringen vil bli fjernet og ulagret data vil gå tapt.',
		() => {
			deactivateAreaMode();
		},
		undefined,
		{
			labels: {
				confirm: 'Ja, gå ut av områdemodus',
				cancel: 'Nei, ta meg tilbake.',
				title: 'Avslutt områdemodus'
			},
			type: 'warning'
		}
	);
}

/** Hard exit — remove polygon, clear state. */
export function deactivateAreaMode(): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	removePolygonEntity(viewer);

	areaModeState.reset();
	mapToolsStore.update((store) => {
		store.selectedMapEntities = [];
		return store;
	});

	document.removeEventListener('keydown', handleAreaModeEsc, false);
}

// ── Minimize / maximize the side panel ─────────────────────────────────────

/**
 * Re-exported thin wrappers around the state mutators so callers don't need
 * to know whether the panel state lives on the class or somewhere else.
 */
export function minimizeAreaPanel(): void {
	areaModeState.minimize();
}

export function maximizeAreaPanel(): void {
	areaModeState.maximize();
}

// ── Internal ────────────────────────────────────────────────────────────────

function removePolygonEntity(viewer: Viewer): void {
	const entity = areaModeState.polygonEntity;
	if (entity) {
		try {
			viewer.entities.remove(entity);
		} catch {
			// already removed — ignore.
		}
	}
}

function handleAreaModeEsc(e: KeyboardEvent): void {
	if (e.key !== 'Escape' && e.key !== 'Esc') return;
	if (!areaModeState.active) return;
	// Defer to any active tool's own Esc handler.
	if (get(isAnyToolEnabled)) return;
	requestExitAreaMode();
}
